import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  ArrowLeft,
  Search,
  Filter,
  Calendar,
  ChevronDown,
  X,
  CheckCircle2,
  Clock,
  AlertCircle,
  Users,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Supabase client initialization
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const ACTIVITY_TYPES = [
  { id: 'all', label: 'Tüm Aktivite', color: 'gray' },
  { id: 'create', label: 'Oluşturma', color: 'green' },
  { id: 'update', label: 'Güncelleme', color: 'blue' },
  { id: 'delete', label: 'Silme', color: 'red' },
];

const SORT_OPTIONS = [
  { id: 'newest', label: 'En Yeni', field: 'created_at', order: 'desc' },
  { id: 'oldest', label: 'En Eski', field: 'created_at', order: 'asc' },
];

const Activities = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortOption, setSortOption] = useState('newest');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerPage = 10;

  // Aktiviteleri getir
  const fetchActivities = async (isLoadMore = false) => {
    try {
      setIsRefreshing(!isLoadMore);
      const currentPage = isLoadMore ? page : 1;

      let query = supabase
        .from('activities')
        .select(`
          *,
          user_profiles!activities_user_id_fkey (
            id,
            first_name,
            last_name,
            profile_photo_url,
            job_position
          ),
          tasks:tasks!activities_task_id_fkey(
            id,
            title
          )
        `)
        .order('created_at', { ascending: sortOption === 'oldest' });

      // Filtreler
      if (typeFilter !== 'all') {
        query = query.eq('activity_type', typeFilter);
      }

      if (searchQuery) {
        query = query.or(
          `description.ilike.%${searchQuery}%`,
          `user_profiles!activities_user_id_fkey(first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%)`,
          `tasks(title.ilike.%${searchQuery}%)`
        );
      }

      if (dateFilter.start) {
        query = query.gte('created_at', dateFilter.start);
      }

      if (dateFilter.end) {
        query = query.lte('created_at', dateFilter.end);
      }

      // Sayfalama
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      query = query.range(from, to);

      const { data, error } = await query;

      if (error) throw error;

      const formattedActivities = data.map(activity => ({
        id: activity.id,
        user: {
          id: activity.user_profiles?.id,
          name: activity.user_profiles ? 
            `${activity.user_profiles.first_name} ${activity.user_profiles.last_name}` : 
            'Anonim Kullanıcı',
          avatar: activity.user_profiles?.profile_photo_url,
          position: activity.user_profiles?.job_position
        },
        type: activity.activity_type,
        description: activity.description,
        taskTitle: activity.tasks?.title,
        task_id: activity.tasks?.id,
        createdAt: new Date(activity.created_at)
      }));

      if (!isLoadMore) {
        setActivities(formattedActivities);
        setPage(1);
      } else {
        setActivities(prev => [...prev, ...formattedActivities]);
        setPage(currentPage + 1);
      }

      setHasMore(data.length === itemsPerPage);

    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error('Aktiviteler yüklenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // İlk yükleme
  useEffect(() => {
    fetchActivities();
  }, [typeFilter, sortOption, searchQuery, dateFilter]);

  // Realtime updates
  useEffect(() => {
    const subscription = supabase
      .channel('activities_channel')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'activities'
      }, () => {
        fetchActivities();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Yükleme durumu için skeleton
  const ActivitySkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="animate-pulse space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mt-2" />
          </div>
        </div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="flex items-center gap-2">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors mt-2 mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Geri Dön</span>
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        {/* Başlık ve Araçlar */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Görev Arşivi
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Sistemdeki tüm aktiviteleri görüntüleyin
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Aktivite ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${
                  showFilters
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Filter className="w-5 h-5" />
              </button>

              <button
                onClick={() => fetchActivities()}
                disabled={isRefreshing}
                className={`p-2 rounded-lg transition-colors text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  isRefreshing ? 'animate-spin' : ''
                }`}
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filtreler */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4 overflow-hidden"
              >
                <div className="p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Aktivite Tipi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Aktivite Tipi
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {ACTIVITY_TYPES.map((type) => (
                          <button
                            key={type.id}
                            onClick={() => setTypeFilter(type.id)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                              typeFilter === type.id
                                ? `bg-${type.color}-100 dark:bg-${type.color}-900/30 text-${type.color}-700 dark:text-${type.color}-400 ring-2 ring-${type.color}-500 dark:ring-${type.color}-400`
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Sıralama */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Sıralama
                      </label>
                      <select
                        value={sortOption}
                        onChange={(e) => setSortOption(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {SORT_OPTIONS.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Tarih Aralığı */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tarih Aralığı
                      </label>
                      <div className="space-y-2">
                        <input
                          type="date"
                          value={dateFilter.start}
                          onChange={(e) => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        <input
                          type="date"
                          value={dateFilter.end}
                          onChange={(e) => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Aktif Filtreler ve Sıfırlama */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between">
                    <div className="flex flex-wrap gap-2">
                      {typeFilter !== 'all' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                          Tip: {ACTIVITY_TYPES.find(t => t.id === typeFilter)?.label}
                          <button onClick={() => setTypeFilter('all')}>
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                      {dateFilter.start && dateFilter.end && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded-full">
                          Tarih Aralığı
                          <button onClick={() => setDateFilter({ start: '', end: '' })}>
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => {
                        setTypeFilter('all');
                        setSortOption('newest');
                        setDateFilter({ start: '', end: '' });
                        setSearchQuery('');
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
                    >
                      <X className="w-4 h-4" />
                      Filtreleri Sıfırla
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Aktivite Listesi */}
        <div className="p-6">
          <div className="space-y-4">
            {isLoading ? (
              // Yükleme durumu
              Array.from({ length: 5 }).map((_, index) => (
                <ActivitySkeleton key={index} />
              ))
            ) : activities.length > 0 ? (
              <>
                {activities.map((activity) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200 relative after:absolute after:bottom-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-b after:from-transparent after:to-white dark:after:to-gray-800 after:rounded-b-lg after:-mb-4"
                  >
                    <div className="flex items-start gap-4">
                      
                      <div className="flex-shrink-0">
                        {activity.user.avatar ? (
                          <img
                            src={activity.user.avatar}
                            alt={activity.user.name}
                            className="h-10 w-10 rounded-full object-cover ring-2 ring-white dark:ring-gray-700"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center ring-2 ring-white dark:ring-gray-700">
                            <span className="text-white font-medium text-lg">
                              {activity.user.name.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {activity.user.name}
                            </p>
                            {activity.user.position && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {activity.user.position}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <Calendar className="w-3 h-3 mr-3 mb-4" />

                            <span>
                              

                              (<time dateTime={activity.createdAt.toISOString()}>
                                {activity.createdAt.toLocaleDateString('tr-TR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                })}{' '} 
                                {activity.createdAt.toLocaleTimeString('tr-TR', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              ) 
                              </time>

                              <p className="text-[10px] text-gray-500 dark:text-gray-400">{formatDistanceToNow(activity.createdAt, { addSuffix: true, locale: tr })}{' '}</p>
                                

                            </span>
                          </div>
                        </div>

                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                          {activity.description}
                          {activity.taskTitle && activity.task_id && (
                            <Link
                              to={`/tasks?taskId=${activity.task_id}`}
                              className="inline-flex items-center ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800/30 transition-colors"
                            >
                              {activity.taskTitle}
                            </Link>
                          )}
                        </p>

                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            activity.type === 'create'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : activity.type === 'update'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          }`}>
                            {activity.type === 'create' ? 'Oluşturma' :
                             activity.type === 'update' ? 'Güncelleme' : 'Silme'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}

                {/* Daha Fazla Yükle */}
                {hasMore && (
                  <div className="text-center mt-6">
                    <button
                      onClick={() => fetchActivities(true)}
                      className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                    >
                      Daha Fazla Göster
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {searchQuery
                    ? `"${searchQuery}" için sonuç bulunamadı`
                    : 'Aktivite Bulunamadı'}
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {searchQuery ? (
                    <>
                      Arama kriterleri:
                      <ul className="mt-2 list-disc list-inside">
                        <li>Aktivite açıklaması</li>
                        <li>Görev başlığı</li>
                        <li>Kullanıcı adı ve soyadı</li>
                      </ul>
                      <p className="mt-2">
                        Farklı anahtar kelimeler kullanarak tekrar deneyebilirsiniz.
                      </p>
                    </>
                  ) : (
                    'Seçilen filtreler için aktivite kaydı bulunmuyor'
                  )}
                </p>
                {(typeFilter !== 'all' || dateFilter.start || dateFilter.end) && (
                  <button
                    onClick={() => {
                      setTypeFilter('all');
                      setDateFilter({ start: '', end: '' });
                      setSearchQuery('');
                    }}
                    className="mt-4 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  >
                    Filtreleri Temizle
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Activities; 